import { Building2, Globe, Star, Ban, RotateCcw } from "lucide-react";
import { cn } from "@/dentlab/src/lib/utils";
import { ExtendedCatalog } from "../../../store/Catalogs/types";
import { TooltipWrapper } from "../../../components/ui/tooltip-wrapper";

const REGION_NAMES: Record<number, string> = {
	1: "Schweiz",
	2: "Deutschland",
	3: "Österreich",
};

interface CatalogRowProps {
	catalog: ExtendedCatalog;
	isSelected: boolean;
	isDefault: boolean;
	onSelect: (catalogId: string | null) => void;
	onSetDefault: (catalog: ExtendedCatalog, e: React.MouseEvent) => void;
	onToggleActivation: (catalog: ExtendedCatalog, e: React.MouseEvent) => void;
}

export const CatalogRow: React.FC<CatalogRowProps> = ({
	catalog,
	isSelected,
	isDefault,
	onSelect,
	onSetDefault,
	onToggleActivation,
}) => {
	const isCustom = catalog.catalog_type === "custom";
	const TypeIcon = isCustom ? Building2 : Globe;
	const isDeactivated = !!catalog.is_deactivated;
	const regionName =
		catalog.region != null
			? REGION_NAMES[catalog.region] ?? `Region ${catalog.region}`
			: null;

	return (
		<div
			className={cn(
				"group relative flex items-center rounded-lg transition-colors",
				isSelected
					? "bg-blue-50/80 ring-1 ring-inset ring-blue-100"
					: "hover:bg-gray-100/70"
			)}
		>
			{isSelected && (
				<span className="absolute inset-y-1.5 left-0 z-10 w-0.5 rounded-full bg-blue-500" />
			)}

			<button
				type="button"
				onClick={() => onSelect(catalog.id)}
				className="flex min-w-0 flex-1 items-center gap-2.5 rounded-lg px-2.5 py-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
			>
				<TypeIcon
					className={cn(
						"h-4 w-4 flex-shrink-0",
						isDeactivated
							? "text-gray-300"
							: isSelected
								? "text-blue-500"
								: "text-gray-400"
					)}
				/>
				<div className="min-w-0 flex-1">
					<div className="flex items-center gap-1.5">
						<span
							className={cn(
								"truncate text-sm font-medium",
								isDeactivated
									? "text-gray-400 line-through decoration-gray-300"
									: isSelected
										? "text-blue-700"
										: "text-gray-800"
							)}
						>
							{catalog.name}
						</span>
						{isDefault && !isDeactivated && (
							<Star
								aria-hidden
								className="h-3 w-3 flex-shrink-0 fill-amber-400 text-amber-400 transition-opacity group-hover:opacity-0"
							/>
						)}
						{isDeactivated && (
							<span className="flex-shrink-0 rounded-full bg-gray-100 px-1.5 py-px text-[10px] font-medium leading-4 text-gray-500 ring-1 ring-inset ring-gray-200">
								Deaktiviert
							</span>
						)}
					</div>
					{regionName && (
						<span className="mt-0.5 block truncate text-xs text-gray-400">
							{regionName}
						</span>
					)}
				</div>
			</button>

			{/* Floating action toolbar — revealed on hover (always shown while
			    deactivated so the catalog can be reactivated). */}
			<div
				className={cn(
					"absolute right-1.5 top-1/2 z-10 -translate-y-1/2 items-center gap-0.5 rounded-md bg-white p-0.5 shadow-sm ring-1 ring-gray-200/70",
					isDeactivated
						? "flex"
						: "hidden focus-within:flex group-hover:flex"
				)}
			>
				{!isDeactivated && (
					<TooltipWrapper
						title={
							isDefault
								? "Als Standard entfernen"
								: "Als Standard setzen"
						}
					>
						<button
							type="button"
							onClick={(e) => onSetDefault(catalog, e)}
							aria-label={
								isDefault
									? `${catalog.name} als Standard entfernen`
									: `${catalog.name} als Standard setzen`
							}
							className="rounded p-1 text-gray-400 transition hover:bg-amber-50 hover:text-amber-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
						>
							<Star
								className={cn(
									"h-3.5 w-3.5",
									isDefault && "fill-amber-400 text-amber-400"
								)}
							/>
						</button>
					</TooltipWrapper>
				)}
				<TooltipWrapper
					title={
						isDeactivated
							? "Katalog aktivieren"
							: "Katalog deaktivieren"
					}
				>
					<button
						type="button"
						onClick={(e) => onToggleActivation(catalog, e)}
						aria-label={
							isDeactivated
								? `Katalog ${catalog.name} aktivieren`
								: `Katalog ${catalog.name} deaktivieren`
						}
						data-testid={`catalog-activation-toggle-${catalog.id}`}
						className={cn(
							"rounded p-1 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
							isDeactivated
								? "text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"
								: "text-gray-400 hover:bg-red-50 hover:text-red-500"
						)}
					>
						{isDeactivated ? (
							<RotateCcw className="h-3.5 w-3.5" />
						) : (
							<Ban className="h-3.5 w-3.5" />
						)}
					</button>
				</TooltipWrapper>
			</div>
		</div>
	);
};
